import React, { useMemo, useState, useCallback } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAppSelector, useAppDispatch } from '../../hooks/redux'
import useChangeRoutPath from '../../hooks/changeRoutPath'
import {
  NavBarContainer,
  LeftSide,
  NavElement,
  RightSide,
  FilterContainer,
  SortContainer,
  CreateIndicator,
  Container,
  Filter,
} from './Subject.style'
import { NAVIGATION } from '../../constants/pathName'
import IndicatorsSignalTable from '../../components/tables/main/indicators-signals-table/IndicatorsSignalTable'
import { IndicatorsSignalTemp } from '../../api/tempData'
import SubjectEdit from './SubjectEdit'
import { EmptyTableIcon } from '../../assets/icons/emptyTableIcon'
import { SCALE_TYPE_COLORS, signalColorsByLetter } from '../../constants/colors'
import { MAX_PER_PAGE } from '../../constants/other'
import { RAG_SPECIAL, RAG_SPECIAL_REVERT } from '../biomarkers/[id]/components/FIltersComponent'
import { BackgroundContainer } from './Subject.styles'
import { updateCurrent } from '../../redux/slice/currentItem'
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material'
import { getSubjectId } from '../biomarkers/[id]/IndicatorsAndSignals'

// =================== HELPERS ===================

export const EmptyTable = ({ name }: { name: string }) => (
  <BackgroundContainer>
    <div style={{ display: 'block', textAlign: 'center' }}>
      <p>Add a first {name || 'item'} to see the table</p>
      <EmptyTableIcon />
    </div>
  </BackgroundContainer>
)

export const getScaleTypeColor = (format: string) => {
  const SPEC = [
    signalColorsByLetter.G,
    signalColorsByLetter.A,
    {
      background: 'rgb(252,242,234)',
      color: 'rgb(242,196,149)',
      textColor: 'rgb(216,149,61)',
      name: 'Light Red',
      shortName1: 'mH',
      shortName2: 'mH',
    },
    signalColorsByLetter.R,
    {
      background: 'rgba(139,0,0,0.76)',
      color: 'darkred',
      textColor: 'white',
      borderColor: '#8b0000ab',
      name: 'Dark Red',
      shortName2: 'MH',
    },
  ]

  if (format === RAG_SPECIAL_REVERT) return [...SPEC].reverse()
  if (format === RAG_SPECIAL || !/RAG|GAR|RAGAR/gi.test(format)) return SPEC

  return (
    format.toUpperCase().split('').map((type) => ({
      ...SCALE_TYPE_COLORS[type],
      name: type,
    })) || []
  )
}

export const getScaleTypeByIndex = (indicator: any, index: number) => {
  const scaleType = indicator?.scaleType?.name || 'RAGAG'
  const colors = getScaleTypeColor(scaleType)
  const type = scaleType[index] || 'GREY'

  return { type, color: colors[index] || signalColorsByLetter.GREY || {} }
}

export const MAX_URL_LENGTH = 30

// =================== MAIN PAGE ===================

const SubjectPage = () => {
  const dispatch = useAppDispatch()
  const { navigateTo } = useChangeRoutPath()
  const userNavigate = useAppSelector((state) => state.user.navigate)
  const { currentItem } = useAppSelector((state) => state)
  const { subjects } = useAppSelector((state) => state.data)
  const { pathname } = useLocation()

  const [filter, setFilter] = useState({
    sort: '_id',
    perPage: MAX_PER_PAGE,
    search: '',
    direction: 'desc',
    filter: {},
  })

  const [language, setLanguage] = useState<string>('en')
  const [isAddLanguageModalOpen, setIsAddLanguageModalOpen] = useState(false)
  const [selectedNewLanguage, setSelectedNewLanguage] = useState('')

  const currentSubject = useMemo(() => {
    const id = pathname.split('/')[1]
    return subjects[id]
  }, [pathname, subjects])

  const availableLanguages = useMemo(() => {
    const name = currentItem?.data?.name
    return name && typeof name === 'object' ? Object.keys(name) : ['en']
  }, [currentItem?.data?.name])

  const handleRemoveLanguage = useCallback((langToRemove: string) => {
    if (availableLanguages.length <= 1) {
      alert('Cannot remove the last language')
      return
    }
    
    if (langToRemove === 'en') {
      alert('Cannot remove English language')
      return
    }
    
    if (window.confirm(`Are you sure you want to remove language "${langToRemove.toUpperCase()}"?`)) {
      // Удаляем язык из name
      const updatedName = { ...currentItem?.data?.name }
      delete updatedName[langToRemove]
      
      // Удаляем язык из description
      const updatedDescription = { ...currentItem?.data?.description }
      delete updatedDescription[langToRemove]
      
      // Обновляем данные в Redux
      dispatch(updateCurrent({
        name: updatedName,
        description: updatedDescription
      }))
      
      // Если удаляемый язык был активным, переключаемся на английский
      if (language === langToRemove) {
        setLanguage('en')
      }
    }
  }, [availableLanguages.length, currentItem?.data?.name, currentItem?.data?.description, dispatch, language])

  const handleAddLanguage = useCallback(() => {
    const code = selectedNewLanguage.toLowerCase().trim()
    if (!code || code.length > 2 || availableLanguages.includes(code) || !currentItem?.data) return

    const newNameObj =
      typeof currentItem.data.name === 'object'
        ? { ...currentItem.data.name, [code]: '' }
        : { en: currentItem.data.name || '', [code]: '' }

    const newDescriptionObj =
        typeof currentItem.data.description === 'object'
          ? { ...currentItem.data.description, [code]: '' }
          : { en: currentItem.data.description || '', [code]: '' }

    dispatch(updateCurrent({ name: newNameObj, description: newDescriptionObj }))
    setLanguage(code)
    setIsAddLanguageModalOpen(false)
    setSelectedNewLanguage('')
  }, [dispatch, selectedNewLanguage, availableLanguages, currentItem])

  const handleCreate = (suburl: string) => {
    navigateTo([...userNavigate, suburl])
  }

  const isTheme = !!currentSubject?._id

  return (
    <>
      {/* Language Switcher */}
      <LeftSide style={{ justifyContent: 'flex-start', marginBottom: 16, display: 'flex', alignItems: 'center' }}>
        {availableLanguages.map((lang, index) => (
          <div key={lang} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            marginRight: index < availableLanguages.length - 1 ? 8 : 0 
          }}>
            <NavElement
              as="span"
              selected={language === lang}
              onClick={() => setLanguage(lang)}
              style={{ cursor: 'pointer', marginRight: 4 }}
            >
              {lang.toUpperCase()}
            </NavElement>
            {lang !== 'en' && availableLanguages.length > 1 && (
              <span
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemoveLanguage(lang)
                }}
                style={{
                  marginLeft: 4,
                  cursor: 'pointer',
                  color: '#ff4444',
                  fontSize: '12px',
                  fontWeight: 'bold'
                }}
              >
                ×
              </span>
            )}
          </div>
        ))}
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIsAddLanguageModalOpen(true)}
          style={{ marginLeft: 8, fontSize: '12px', padding: '4px 8px' }}
        >
          + Add Language
        </Button>
      </LeftSide>

      {/* Title */}
      <SubjectEdit language={language} />

      {/* Navigation */}
      {isTheme && <LeftNavbar />}

      {/* Table */}
      {isTheme ? (
        <IndicatorsSignalTable
          setFilter={setFilter}
          filter={filter}
          data={IndicatorsSignalTemp}
          language={language}
        />
      ) : (
        <EmptyTable name="subject" />
      )}

      {/* Add Language Modal */}
      <Dialog open={isAddLanguageModalOpen} onClose={() => setIsAddLanguageModalOpen(false)}>
        <DialogTitle>Add New Language</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Language Code"
            value={selectedNewLanguage}
            onChange={(e) => setSelectedNewLanguage(e.target.value)}
            placeholder="e.g., fr, es, ru"
            inputProps={{ maxLength: 2 }}
            style={{ marginTop: 16 }}
            helperText="Enter 2-letter language code (e.g., fr for French, es for Spanish)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddLanguageModalOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddLanguage}
            variant="contained"
            disabled={
              !selectedNewLanguage ||
              selectedNewLanguage.length > 2 ||
              availableLanguages.includes(selectedNewLanguage.toLowerCase())
            }
          >
            Add Language
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

// =================== LEFT NAVBAR ===================

const LeftNavbar = () => {
  const { pathname } = useLocation()
  const subjectId = '/' + getSubjectId()

  const paths = [
    { ...NAVIGATION.INDACATORS_SIGNALS, name: 'Biomarkers' },
    { ...NAVIGATION.INTERPRETATIONS, name: 'Diseases' },
    { ...NAVIGATION.RECOMENDATIONS, name: 'Recommendations' },
  ]

  return (
    <LeftSide data-left-side>
      {paths.map((path) => {
        const isSelected = pathname.includes(path.url)
        return (
          <NavElement key={path.id} selected={isSelected}>
            {isSelected ? <span>{path.name}</span> : <Link to={`${subjectId}/${path.url}`}>{path.name}</Link>}
          </NavElement>
        )
      })}
    </LeftSide>
  )
}

export { LeftNavbar }
export default SubjectPage
